import { DEFAULT_LOCALE as DEFAULT_ORGANIZATION_LOCALE } from '@/lib/locale';

export const DEFAULT_ORGANIZATION_TIMEZONE = 'America/Santiago';
const DEFAULT_TIMEZONE_LOCALE = DEFAULT_ORGANIZATION_LOCALE.locale;

type DateLike = string | number | Date;

type DateFormatOptions = {
  timeZone?: string | null;
  locale?: string;
  dateStyle?: 'full' | 'long' | 'medium' | 'short';
};

type TimeFormatOptions = {
  timeZone?: string | null;
  locale?: string;
  hour12?: boolean;
};

type DateTimeFormatOptions = {
  timeZone?: string | null;
  locale?: string;
  dateStyle?: 'full' | 'long' | 'medium' | 'short';
  timeStyle?: 'full' | 'long' | 'medium' | 'short';
  hour12?: boolean;
};

function asDate(value: DateLike) {
  return value instanceof Date ? value : new Date(value);
}

function timeZoneOffsetMinutes(date: Date, timeZone: string): number {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  const parts = formatter.formatToParts(date);
  const get = (type: string) => Number(parts.find((part) => part.type === type)?.value ?? '0');
  const asUtc = Date.UTC(
    get('year'),
    get('month') - 1,
    get('day'),
    get('hour'),
    get('minute'),
    get('second'),
  );

  return (asUtc - date.getTime()) / 60_000;
}

function getDateTimePartsInTimezone(value: DateLike, timeZone?: string | null) {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: resolveOrganizationTimezone(timeZone),
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
  const parts = formatter.formatToParts(asDate(value));
  const get = (type: string) => Number(parts.find((part) => part.type === type)?.value ?? '0');

  return {
    year: get('year'),
    month: get('month'),
    day: get('day'),
    hour: get('hour'),
    minute: get('minute'),
  };
}

function getDatePartsInTimezone(value: DateLike, timeZone?: string | null) {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: resolveOrganizationTimezone(timeZone),
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });

  const parts = formatter.formatToParts(asDate(value));
  const year = Number(parts.find((part) => part.type === 'year')?.value ?? '0');
  const month = Number(parts.find((part) => part.type === 'month')?.value ?? '0');
  const day = Number(parts.find((part) => part.type === 'day')?.value ?? '0');

  return { year, month, day };
}

export function resolveOrganizationTimezone(timezone?: string | null) {
  const candidate = timezone?.trim();
  if (!candidate) {
    return DEFAULT_ORGANIZATION_TIMEZONE;
  }

  try {
    new Intl.DateTimeFormat('en-US', { timeZone: candidate }).format(new Date());
    return candidate;
  } catch {
    return DEFAULT_ORGANIZATION_TIMEZONE;
  }
}

export function detectBrowserTimezone() {
  if (typeof Intl === 'undefined' || typeof Intl.DateTimeFormat !== 'function') {
    return DEFAULT_ORGANIZATION_TIMEZONE;
  }

  try {
    const detected = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return resolveOrganizationTimezone(detected);
  } catch {
    return DEFAULT_ORGANIZATION_TIMEZONE;
  }
}

export function formatDateInTimezone(
  value: DateLike,
  options: DateFormatOptions = {},
) {
  const { locale = DEFAULT_TIMEZONE_LOCALE, dateStyle = 'full' } = options;

  return new Intl.DateTimeFormat(locale, {
    dateStyle,
    timeZone: resolveOrganizationTimezone(options.timeZone),
  }).format(asDate(value));
}

export function formatTimeInTimezone(
  value: DateLike,
  options: TimeFormatOptions = {},
) {
  const { locale = DEFAULT_TIMEZONE_LOCALE, hour12 = false } = options;

  return new Intl.DateTimeFormat(locale, {
    hour: '2-digit',
    minute: '2-digit',
    hour12,
    timeZone: resolveOrganizationTimezone(options.timeZone),
  }).format(asDate(value));
}

export function formatDateTimeInTimezone(
  value: DateLike,
  options: DateTimeFormatOptions = {},
) {
  const {
    locale = DEFAULT_TIMEZONE_LOCALE,
    dateStyle = 'medium',
    timeStyle = 'short',
    hour12 = false,
  } = options;

  return new Intl.DateTimeFormat(locale, {
    dateStyle,
    timeStyle,
    hour12,
    timeZone: resolveOrganizationTimezone(options.timeZone),
  }).format(asDate(value));
}

/** Convierte un instante absoluto al valor esperado por datetime-local. */
export function formatDateTimeLocalInTimezone(
  value: DateLike,
  timeZone?: string | null,
) {
  const { year, month, day, hour, minute } = getDateTimePartsInTimezone(value, timeZone);
  return [
    String(year).padStart(4, '0'),
    String(month).padStart(2, '0'),
    String(day).padStart(2, '0'),
  ].join('-') + `T${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

/** Instante absoluto correspondiente a una fecha/hora de pared del tenant. */
export function zonedDateTimeToUtc(
  date: string,
  minutes: number,
  timeZone?: string | null,
) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!match || !Number.isInteger(minutes) || minutes < 0 || minutes >= 24 * 60) {
    throw new RangeError('Invalid tenant-local date/time');
  }

  const [, yearText, monthText, dayText] = match;
  const year = Number(yearText);
  const month = Number(monthText);
  const day = Number(dayText);
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  const resolvedTimezone = resolveOrganizationTimezone(timeZone);
  const naiveUtc = Date.UTC(year, month - 1, day, hours, mins);
  const firstOffset = timeZoneOffsetMinutes(new Date(naiveUtc), resolvedTimezone);
  let result = new Date(naiveUtc - firstOffset * 60_000);

  const secondOffset = timeZoneOffsetMinutes(result, resolvedTimezone);
  if (secondOffset !== firstOffset) {
    result = new Date(naiveUtc - secondOffset * 60_000);
  }

  return result;
}

/** Interpreta YYYY-MM-DDTHH:mm como wall clock del tenant, nunca del browser. */
export function dateTimeLocalToUtc(
  value: string,
  timeZone?: string | null,
) {
  const match = /^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2})$/.exec(value);
  if (!match) {
    throw new RangeError('Invalid datetime-local value');
  }

  const [, date, hourText, minuteText] = match;
  const minutes = Number(hourText) * 60 + Number(minuteText);
  const result = zonedDateTimeToUtc(date, minutes, timeZone);

  if (formatDateTimeLocalInTimezone(result, timeZone) !== value) {
    throw new RangeError('Tenant-local date/time does not exist in timezone');
  }

  return result;
}

export function getDateKeyInTimezone(
  value: DateLike,
  timeZone?: string | null,
) {
  const { year, month, day } = getDatePartsInTimezone(value, timeZone);
  return [
    String(year).padStart(4, '0'),
    String(month).padStart(2, '0'),
    String(day).padStart(2, '0'),
  ].join('-');
}

export function getRelativeDateKeyInTimezone(
  base: DateLike,
  dayOffset: number,
  timeZone?: string | null,
) {
  const { year, month, day } = getDatePartsInTimezone(base, timeZone);
  const shifted = new Date(Date.UTC(year, month - 1, day + dayOffset));
  return shifted.toISOString().slice(0, 10);
}
